const { applySlotSizeFromPrebid } = require('./adSizeUtils');
const {
  PREBID_MANAGED_TARGETING_KEYS,
  applyPrebidTargetingToSlots,
  clearSlotTargeting
} = require('./gptConfig');

function getSlotId(slot) {
  return slot && typeof slot.getSlotElementId === 'function'
    ? String(slot.getSlotElementId() || '').trim()
    : '';
}

function getRegisteredAdUnits(pbjs) {
  if (!pbjs) return [];
  if (typeof pbjs.getAdUnits === 'function') {
    const units = pbjs.getAdUnits();
    return Array.isArray(units) ? units : [];
  }
  return Array.isArray(pbjs.adUnits) ? pbjs.adUnits : [];
}

function hasAdUnit(pbjs, slotId) {
  return getRegisteredAdUnits(pbjs).some((unit) => unit && unit.code === slotId);
}

function getBidCountForCode(bidResponses, slotId) {
  if (!bidResponses || typeof bidResponses !== 'object') return 0;
  const response = bidResponses[slotId];
  if (Array.isArray(response)) return response.length;
  return response && Array.isArray(response.bids) ? response.bids.length : 0;
}

function createAdAuctionCoordinator(options = {}) {
  const pbjs = options.pbjs || null;
  const googletag = options.googletag || null;
  const diagnostics = options.diagnostics || null;
  const timeoutMs = Number.isFinite(Number(options.timeoutMs))
    ? Math.max(1, Number(options.timeoutMs))
    : 1500;
  const failsafeBufferMs = Number.isFinite(Number(options.failsafeBufferMs))
    ? Math.max(0, Number(options.failsafeBufferMs))
    : Math.max(100, 3000 - timeoutMs);
  const runtimeDocument = options.runtimeDocument
    || (typeof document !== 'undefined' ? document : null);
  const managedTargetingKeys = Array.isArray(options.managedTargetingKeys)
    ? options.managedTargetingKeys
    : PREBID_MANAGED_TARGETING_KEYS;
  const registrations = new Map();
  const inFlight = new Map();
  const timers = new Set();
  let destroyed = false;

  function record(type, metadata) {
    if (diagnostics && typeof diagnostics.record === 'function') {
      diagnostics.record(type, metadata);
    }
  }

  function registerSlot({
    slotId,
    gptSlot,
    createAdUnit,
    placement = {},
    requireElement = true,
    applyPrebidSize
  } = {}) {
    const normalizedSlotId = String(slotId || getSlotId(gptSlot)).trim();
    if (!normalizedSlotId || !gptSlot || destroyed) {
      return false;
    }

    const existing = registrations.get(normalizedSlotId);
    registrations.set(normalizedSlotId, {
      slotId: normalizedSlotId,
      gptSlot,
      createAdUnit: typeof createAdUnit === 'function'
        ? createAdUnit
        : existing && existing.createAdUnit,
      placement: { ...(existing?.placement || {}), ...(placement || {}) },
      requireElement: requireElement !== false,
      applyPrebidSize: applyPrebidSize === undefined
        ? existing?.applyPrebidSize !== false
        : applyPrebidSize !== false,
      adUnitRegistered: existing?.adUnitRegistered || hasAdUnit(pbjs, normalizedSlotId)
    });
    record('slot_registered', { slotId: normalizedSlotId, ...placement });
    return true;
  }

  function isSlotPresent(registration) {
    if (!registration.requireElement || !runtimeDocument) return true;
    return !!runtimeDocument.getElementById(registration.slotId);
  }

  function ensureAdUnit(registration) {
    if (!pbjs || typeof pbjs.addAdUnits !== 'function') return false;
    if (registration.adUnitRegistered || hasAdUnit(pbjs, registration.slotId)) {
      registration.adUnitRegistered = true;
      return true;
    }
    if (typeof registration.createAdUnit !== 'function') return false;

    const unit = registration.createAdUnit();
    if (!unit || unit.code !== registration.slotId) {
      record('ad_unit_invalid', { slotId: registration.slotId });
      return false;
    }
    pbjs.addAdUnits([unit]);
    registration.adUnitRegistered = true;
    record('ad_unit_registered', {
      slotId: registration.slotId,
      bidderCount: Array.isArray(unit.bids) ? unit.bids.length : 0
    });
    return true;
  }

  function queueGpt(callback) {
    if (googletag && googletag.cmd && typeof googletag.cmd.push === 'function') {
      googletag.cmd.push(callback);
      return;
    }
    callback();
  }

  function requestSlots(slotIds, requestOptions = {}) {
    if (destroyed) {
      return Promise.resolve({ status: 'destroyed', slotIds: [] });
    }

    const requestedIds = [...new Set((Array.isArray(slotIds) ? slotIds : [slotIds])
      .map((slotId) => String(slotId || '').trim())
      .filter(Boolean))];
    const activePromises = [];
    const available = [];

    requestedIds.forEach((slotId) => {
      const registration = registrations.get(slotId);
      if (!registration) {
        record('auction_skipped_unregistered', { slotId });
        return;
      }
      if (!isSlotPresent(registration)) {
        record('auction_skipped_absent', { slotId });
        return;
      }
      if (inFlight.has(slotId)) {
        activePromises.push(inFlight.get(slotId));
        record('auction_coalesced', { slotId });
        return;
      }
      available.push(registration);
    });

    if (available.length === 0) {
      if (activePromises.length > 0) {
        return activePromises[0];
      }
      return Promise.resolve({ status: 'skipped', slotIds: [] });
    }

    const codes = available.map((registration) => registration.slotId);
    const slots = available.map((registration) => registration.gptSlot);
    const reason = requestOptions.reason || 'initial';
    let resolveRequest;
    const requestPromise = new Promise((resolve) => {
      resolveRequest = resolve;
    });
    codes.forEach((slotId) => inFlight.set(slotId, requestPromise));

    let completed = false;
    let failsafeTimer = null;

    const finish = (status, bidResponses) => {
      if (completed) return;
      completed = true;
      if (failsafeTimer) {
        clearTimeout(failsafeTimer);
        timers.delete(failsafeTimer);
      }

      queueGpt(() => {
        if (destroyed) {
          codes.forEach((slotId) => inFlight.delete(slotId));
          resolveRequest({ status: 'destroyed', slotIds: codes });
          return;
        }

        const liveRegistrations = available.filter((registration) => {
          return registrations.has(registration.slotId) && isSlotPresent(registration);
        });
        const liveCodes = liveRegistrations.map((registration) => registration.slotId);
        const liveSlots = liveRegistrations.map((registration) => registration.gptSlot);
        const removedCodes = codes.filter((slotId) => !liveCodes.includes(slotId));
        removedCodes.forEach(destroy);

        let bidCount = 0;
        const targetedSlotIds = [];
        liveCodes.forEach((slotId, index) => {
          const currentBidCount = status === 'bids_back'
            ? getBidCountForCode(bidResponses, slotId)
            : 0;
          if (currentBidCount > 0) {
            bidCount += 1;
            if (liveRegistrations[index].applyPrebidSize) {
              applySlotSizeFromPrebid(slotId, pbjs);
            }
            applyPrebidTargetingToSlots(pbjs, liveSlots[index]);
            targetedSlotIds.push(slotId);
          }
        });
        record('targeting_applied', {
          slotIds: targetedSlotIds,
          reason,
          refreshCount: requestOptions.refreshCount
        });

        if (googletag && typeof googletag.pubads === 'function') {
          const pubads = googletag.pubads();
          if (pubads && typeof pubads.refresh === 'function') {
            if (liveSlots.length > 0) {
              pubads.refresh(liveSlots);
            }
          }
        }

        const finalStatus = liveSlots.length === 0
          ? 'slot_removed'
          : status === 'bids_back'
            ? (bidCount > 0 ? 'bid' : 'no_bid')
            : status;
        record(liveSlots.length > 0 ? 'gam_request_issued' : 'gam_request_skipped', {
          slotIds: liveCodes,
          skippedSlotIds: removedCodes,
          reason,
          auctionStatus: finalStatus,
          bidCount,
          refreshCount: requestOptions.refreshCount
        });
        codes.forEach((slotId) => inFlight.delete(slotId));
        resolveRequest({
          status: finalStatus,
          slotIds: liveCodes,
          bidCount,
          gamRequested: liveSlots.length > 0
        });
      });
    };

    slots.forEach((slot) => clearSlotTargeting(slot, managedTargetingKeys));
    record('targeting_cleared', {
      slotIds: codes,
      reason,
      refreshCount: requestOptions.refreshCount
    });

    const canUsePrebid = pbjs
      && typeof pbjs.requestBids === 'function'
      && available.every(ensureAdUnit);
    if (!canUsePrebid) {
      record('auction_fallback', { slotIds: codes, reason: 'prebid_unavailable' });
      finish('prebid_unavailable');
      return requestPromise;
    }

    record('auction_requested', {
      slotIds: codes,
      reason,
      timeoutMs,
      refreshCount: requestOptions.refreshCount
    });
    failsafeTimer = setTimeout(() => {
      timers.delete(failsafeTimer);
      record('auction_timed_out', { slotIds: codes, reason });
      finish('timeout');
    }, timeoutMs + failsafeBufferMs);
    timers.add(failsafeTimer);

    try {
      pbjs.requestBids({
        timeout: timeoutMs,
        adUnitCodes: codes,
        bidsBackHandler(bidResponses) {
          record('auction_completed', { slotIds: codes, reason });
          finish('bids_back', bidResponses);
        }
      });
    } catch (error) {
      record('auction_failed', {
        slotIds: codes,
        reason,
        error: error && error.message ? error.message : String(error)
      });
      finish('prebid_error');
    }

    return requestPromise;
  }

  function destroy(slotId) {
    const normalizedSlotId = String(slotId || '').trim();
    if (!normalizedSlotId) return;
    registrations.delete(normalizedSlotId);
    inFlight.delete(normalizedSlotId);
    if (pbjs && typeof pbjs.removeAdUnit === 'function') {
      try {
        pbjs.removeAdUnit(normalizedSlotId);
      } catch (_) {
        // Prebid cleanup is best-effort during DOM removal/reinitialization.
      }
    }
    record('slot_destroyed', { slotId: normalizedSlotId });
  }

  function destroyAll() {
    if (destroyed) return;
    const slotIds = [...registrations.keys()];
    slotIds.forEach(destroy);
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    destroyed = true;
    record('coordinator_destroyed', { slotIds });
  }

  return {
    registerSlot,
    requestInitial(slotIds, requestOptions = {}) {
      return requestSlots(slotIds, { ...requestOptions, reason: requestOptions.reason || 'initial' });
    },
    requestRefresh(slotId, requestOptions = {}) {
      return requestSlots([slotId], { ...requestOptions, reason: requestOptions.reason || 'refresh' });
    },
    destroy,
    destroyAll,
    isInFlight(slotId) {
      return inFlight.has(String(slotId || '').trim());
    },
    getRegisteredSlotIds() {
      return [...registrations.keys()];
    }
  };
}

module.exports = {
  createAdAuctionCoordinator,
  getBidCountForCode,
  getSlotId,
  hasAdUnit
};
